import { FC, Fragment } from "react";
import {
  Menu,
  MenuButton,
  MenuList,
  Button,
  Text,
  Flex,
  Box,
  VStack,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  HStack,
  Divider,
} from "@chakra-ui/react";
import { useStaticQuery, graphql } from "gatsby";
import { ShiIcon } from "src/atoms";

interface LanguageSelectorProps {
  color?: string;
  hoverBg?: string;
}

// ─── Sub-Component: LanguageList ──────────────────────────────────────────
// Refactored into a standalone component to prevent "update during render"
// warnings and ensure stable rendering during Gatsby builds.
interface LanguageListProps {
  regions: any[];
  onItemClick?: () => void;
}

const LanguageList: FC<LanguageListProps> = ({ regions, onItemClick }) => (
  <Box py="space-8">
    {regions.map((region: any, index: number) => (
      <Flex key={index} px="space-24" py="space-8" w="full">
        <Box w="120px" flexShrink={0} pt="space-4">
          <Text color="primary-green" fontWeight="medium" fontSize="sm">
            {region.country}
          </Text>
        </Box>
        <VStack align="start" spacing="space-4" flex={1}>
          {region.languages.map((language: any, langIndex: number) => (
            /*
             * Active-language highlighting removed: all links point to external domains.
             * Re-add when language links point to pages within the same domain.
             */
            <Box
              as="a"
              href={language.url}
              rel="noopener noreferrer"
              key={langIndex}
              display="block"
              w="full"
              px="space-8"
              py="space-4"
              borderRadius="md"
              color="grey-600"
              _hover={{ color: "primary-green", bg: "monochrome-100" }}
              fontSize="sm"
              textAlign="left"
              fontWeight="normal"
              onClick={onItemClick}
            >
              {language.label}
            </Box>
          ))}
        </VStack>
      </Flex>
    ))}
  </Box>
);

const LanguageSelector: FC<LanguageSelectorProps> = ({
  color = "inherit",
  hoverBg = "blackAlpha.200",
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const drawer = useDisclosure();

  // ─── Fetch global language settings from Prismic ──────────────────────────
  const { allPrismicLanguageSettings } =
    useStaticQuery<Gatsby.LanguagesFromSettingsQuery>(graphql`
      query LanguagesFromSettings {
        allPrismicLanguageSettings {
          nodes {
            data {
              module_placeholder {
                text
              }
              body {
                __typename
                ... on PrismicLanguageSettingsDataBodyBlockLanguage {
                  primary {
                    country_name {
                      text
                    }
                  }
                  items {
                    language_label_name
                    language_link {
                      url
                    }
                  }
                }
              }
            }
          }
        }
      }
    `);

  const rawNode = allPrismicLanguageSettings?.nodes?.[0]?.data;
  const rawBody: any[] = rawNode?.body ?? [];
  const modulePlaceholder: string =
    rawNode?.module_placeholder?.text ?? "Select country and language";

  // Build the regions list from the CMS slices.
  // Each region has a country name and a list of languages with a label and
  // a direct external URL — no locale-code resolution or prefix stripping needed.
  const regions = rawBody
    .filter(
      (slice: any) =>
        slice.__typename === "PrismicLanguageSettingsDataBodyBlockLanguage",
    )
    .map((slice: any) => ({
      country: slice.primary?.country_name?.text ?? "",
      languages: (slice.items ?? []).map((item: any) => ({
        label: item.language_label_name ?? "",
        url: item.language_link?.url ?? "",
      })),
    }));

  return (
    <Fragment>
      {/* ─── Mobile View ────── */}
      <Box display={{ base: "block", md: "none" }}>
        <Button
          variant="ghost"
          leftIcon={<ShiIcon name="global" />}
          rightIcon={
            <Box
              as={ShiIcon}
              name="chevron"
              color="currentColor"
              sx={{
                "& path": { fill: "currentColor" },
              }}
              transform={drawer.isOpen ? "rotate(180deg)" : "rotate(0deg)"}
              transition="transform 0.2s ease"
            />
          }
          color={color}
          _hover={{ bg: hoverBg }}
          _active={{ bg: hoverBg }}
          paddingX="space-8"
          height="auto"
          minHeight="32px"
          borderWidth="1px"
          borderColor="white"
          borderRadius="md"
          onClick={drawer.onOpen}
        >
          {modulePlaceholder}
        </Button>

        <Drawer
          isOpen={drawer.isOpen}
          onClose={drawer.onClose}
          placement="bottom"
        >
          <DrawerOverlay />
          <DrawerContent borderTopRadius="xl" maxHeight="80dvh">
            <DrawerHeader
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              paddingY="space-16"
              paddingX="space-24"
            >
              <HStack spacing="space-8">
                <ShiIcon name="global" />
                <Text
                  fontSize="md"
                  fontWeight="medium"
                  color="btn-secondary-text"
                >
                  {modulePlaceholder}
                </Text>
              </HStack>
              <DrawerCloseButton
                size="md"
                position="unset"
                sx={{
                  svg: {
                    width: "12px",
                    height: "12px",
                  },
                }}
              />
            </DrawerHeader>

            <Divider borderColor="grey-200" />

            <DrawerBody px={0} py={0} overflowY="auto">
              <LanguageList
                regions={regions}
                onItemClick={drawer.onClose}
              />
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      </Box>

      {/* ─── Desktop View ───────────────────────────────────────────────── */}
      <Box display={{ base: "none", md: "block" }}>
        <Menu isOpen={isOpen} onClose={onClose} autoSelect={false} placement="top-start" gutter={0}>
          <MenuButton
            as={Button}
            variant="ghost"
            onMouseEnter={onOpen}
            onMouseLeave={onClose}
            leftIcon={<ShiIcon name="global" />}
            rightIcon={
              <Box
                as={ShiIcon}
                name="chevron"
                color="currentColor"
                sx={{
                  "& path": { fill: "currentColor" },
                }}
                transform={isOpen ? "rotate(180deg)" : "rotate(0deg)"}
                transition="transform 0.2s ease"
              />
            }
            color={color}
            _hover={{ bg: hoverBg }}
            _active={{ bg: hoverBg }}
            paddingX="space-8"
            height="auto"
            minHeight="32px"
            borderWidth="1px"
            borderColor="white"
            borderRadius="md"
          >
            {modulePlaceholder}
          </MenuButton>
          <MenuList
            onMouseEnter={onOpen}
            onMouseLeave={onClose}
            minW="300px"
            maxW="400px"
            width="max-content"
            p={0}
            boxShadow="md"
            overflow="hidden"
            borderRadius="md"
            border="1px solid"
            borderColor="grey-200"
          >
            <Box
              px="space-24"
              py="space-16"
              borderBottomWidth="1px"
              borderColor="grey-200"
            >
              <Text
                fontSize="md"
                color="btn-secondary-text"
                fontWeight="medium"
              >
                {modulePlaceholder}
              </Text>
            </Box>
            <LanguageList regions={regions} />
          </MenuList>
        </Menu>
      </Box>
    </Fragment>
  );
};

export default LanguageSelector;
